import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import lottie from 'lottie-web';
import PropTypes from 'prop-types';
import MilestoneTable from './MilestoneTable';
import ProjectListTable from './ProjectListTable';
import './MilestoneDetails.scss';

/* -------------------------------------------------------------------------- */
/*  Empty state (Lottie animation)                                             */
/* -------------------------------------------------------------------------- */

const EmptyState = ({ message }) => {
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useRef(null);

  useEffect(() => {
    import('../../../Assets/images/json/dar_milestone_details_load.json').then(
      ({ default: data }) => setAnimationData(data),
    );
  }, []);

  useEffect(() => {
    if (animationData && animationContainer.current) {
      const anim = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      });
      return () => anim.destroy();
    }
    return undefined;
  }, [animationData]);

  return (
    <div className="ms-empty-state-outer">
      <div className="ms-empty-state">
        <div className="ms-empty-state__animation-card">
          <div ref={animationContainer} className="ms-empty-state__animation" />
          <p className="ms-empty-state__text">{message}</p>
        </div>
      </div>
    </div>
  );
};

EmptyState.propTypes = { message: PropTypes.string.isRequired };

/* -------------------------------------------------------------------------- */
/*  Main page component                                                        */
/* -------------------------------------------------------------------------- */

const MilestoneDetailsMain = () => {
  const tableRef = useRef();
  const location = useLocation();

  const [filterApplied, setFilterApplied] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [milestoneRefreshKey, setMilestoneRefreshKey] = useState(0);

  const deepLinkParams = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return {
      projectId: params.get('projectId') || '',
      projectName: params.get('projectName') || '',
      editType: params.get('editType') || '',
      milestoneId: params.get('milestoneId') || '',
      invoiceId: params.get('invoiceId') || '',
      collectionId: params.get('collectionId') || '',
    };
  }, [location.search]);

  useEffect(() => {
    if (!deepLinkParams.projectId) return;

    setFilterApplied(true);
    setSelectedProjectId(deepLinkParams.projectId);
    setSelectedProject((prev) => ({
      ...(prev || {}),
      ...(deepLinkParams.projectName ? { projectName: deepLinkParams.projectName } : {}),
      projectId: deepLinkParams.projectId,
    }));
  }, [deepLinkParams]);

  const handleFilterApplied = useCallback((firstProject) => {
    setFilterApplied(true);
    if (firstProject?.projectId) {
      setSelectedProject(firstProject);
      setSelectedProjectId(firstProject.projectId);
      setMilestoneRefreshKey((prev) => prev + 1);
    } else {
      setSelectedProject(null);
      setSelectedProjectId(null);
    }
  }, []);

  const handleReset = useCallback(() => {
    setFilterApplied(false);
    setSelectedProject(null);
    setSelectedProjectId(null);
  }, []);

  const handleProjectSelect = useCallback((project) => {
    setFilterApplied(true);
    setSelectedProject(project);
    setSelectedProjectId(project.projectId);
    setMilestoneRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <div className="milestone-details-wrapper">
      <div className="milestone-details-content">
        <ProjectListTable
          onProjectSelect={handleProjectSelect}
          onFilterApplied={handleFilterApplied}
          onReset={handleReset}
          noDataMode={!filterApplied}
          toolBarHeading={filterApplied ? 'Project Details' : 'Milestone Details'}
          initialProjectId={deepLinkParams.projectId}
          initialProjectName={deepLinkParams.projectName}
        />

        {!filterApplied && (
          <EmptyState message="Select the filters above to see the results" />
        )}

        {filterApplied && selectedProjectId && (
          <MilestoneTable
            key={`${selectedProjectId}-${milestoneRefreshKey}`}
            ref={tableRef}
            projectId={selectedProjectId}
            projectName={selectedProject?.projectName}
            initialDialog={deepLinkParams}
          />
        )}
      </div>
    </div>
  );
};

export default MilestoneDetailsMain;
